import { useInfiniteQuery, type UseInfiniteQueryResult, type InfiniteData } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppConfig } from '@config/AppConfigContext';
import { QUERY_DEFAULTS } from '@hooks/requests/utils/use_basic_query';
import { toQueryString, fetchJson, getAuthHeader, joinUrl } from '@hooks/requests/utils/_partner_api_client';
import { PARTNER_LOGS_PATH } from '@hooks/requests/utils/paths';

// ── Types ──────────────────────────────────────────────────────────────────────

/** Raw wire shape returned by GET /api/v1/partner/logs (camelCase — see partners/dtos.py's PartnerApiLogEntryResponse). */
export interface PartnerLogEntry {
  /** Omitted entirely (not just null) for Partner roles — spec FR-013. */
  organizationId?: string | null;
  companyId?: string | null;
  eventType: string;
  status: string;
  statusCode: number;
  /** Composed "Details" column text — what the `search` param matches against (BR-010). */
  details: string;
  requestId?: string | null;
  /** ISO 8601 timestamp. */
  timestamp: string;
  metadata: Record<string, unknown>;
}

export interface PartnerLogsPage {
  events: PartnerLogEntry[];
  /** Opaque cursor for the next page; absent when this is the last page. */
  nextPageToken?: string | null;
}

export interface UsePartnerLogsParams {
  /** AceUp staff only — scopes the request to this organization. Ignored for Partner roles (always their own org). */
  organizationId?: string | null;
  companyId?: string | null;
  /** 'success' | 'error' */
  status?: string | null;
  eventType?: string | null;
  /** Request method filter ('GET' | 'POST' | …) — the page's "Event type" dropdown. */
  httpMethod?: string | null;
  /** Trailing-window preset ('1h' | '24h' | '7d' | '30d'). Ignored when `from`/`to` are both set. */
  range?: string | null;
  /** Custom range start, MM-DD-YYYY. Requires `to`. */
  from?: string | null;
  /** Custom range end, MM-DD-YYYY. Requires `from`. */
  to?: string | null;
  /** Case-insensitive substring match against the composed Details text. */
  search?: string | null;
  pageSize?: number;
  enabled?: boolean;
}

// ── Hook ───────────────────────────────────────────────────────────────────────

/**
 * Fetches every page of Partner API log entries (Logs V1 — sc-44216/sc-44238),
 * newest first, via GET /api/v1/partner/logs.
 *
 * Built on `useInfiniteQuery` rather than `useBasicQuery` + hand-rolled
 * `entries`/`pageToken` state: react-query owns the accumulated pages here,
 * keyed on `params` — so a filter change naturally starts a fresh page one
 * (new cache key) instead of needing a manual reset effect, and a background
 * refetch of an already-fetched page (react-query's `refetchOnWindowFocus`,
 * `<StrictMode>` remounts) replaces that page in place rather than
 * re-appending it. A manual `useEffect(() => setEntries(prev => [...prev,
 * ...data.events]), [data])` can't tell "this is a genuinely new page" apart
 * from "the same page refetched", since `data` gets a new object identity on
 * every successful fetch either way.
 *
 * A custom `from`/`to` pair takes precedence over `range` on the backend —
 * both are still forwarded here rather than the caller pre-resolving one,
 * since only the backend enforces "both or neither" for a custom range.
 */
export const usePartnerLogsQuery = (
  params: UsePartnerLogsParams = {}
): UseInfiniteQueryResult<InfiniteData<PartnerLogsPage>, Error> => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const { allyApi, auth0Audience } = useAppConfig();

  const enabled = Boolean(isAuthenticated && allyApi) && (params.enabled ?? true);

  const queryParams = {
    organization_id: params.organizationId || undefined,
    companyId: params.companyId || undefined,
    status: params.status || undefined,
    eventType: params.eventType || undefined,
    httpMethod: params.httpMethod || undefined,
    range: params.range || undefined,
    from: params.from || undefined,
    to: params.to || undefined,
    search: params.search || undefined,
    pageSize: params.pageSize,
  };

  return useInfiniteQuery<PartnerLogsPage, Error, InfiniteData<PartnerLogsPage>, unknown[], string | undefined>({
    queryKey: [PARTNER_LOGS_PATH, queryParams],
    enabled,
    staleTime: QUERY_DEFAULTS.staleTime,
    retry: QUERY_DEFAULTS.retry,
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.nextPageToken || undefined,
    queryFn: async ({ pageParam }): Promise<PartnerLogsPage> => {
      if (!allyApi) throw new Error('Missing allyApi configuration');

      const authHeader = await getAuthHeader(getAccessTokenSilently, auth0Audience);
      const query = toQueryString({ ...queryParams, pageToken: pageParam });

      return fetchJson<PartnerLogsPage>(
        joinUrl(allyApi, `${PARTNER_LOGS_PATH}${query}`),
        authHeader,
        `Failed to fetch ${PARTNER_LOGS_PATH}`
      );
    },
  });
};
